import { ProfessionalSummaryEntry } from '@applicator/shared'
import { motion } from 'framer-motion'
import React, { useState, useEffect, useMemo, useRef } from 'react'

import CustomCheckbox from '../CustomCheckbox'
import { highlightSearchQuery } from '../../utils/highlightSearchQuery'
import { useVerticalTextareaResize } from '../../utils/useVerticalTextareaResize'
import {
  adoptAccountSummaryVariant,
  normalizeSummaryTextForDedupe,
  type AccountSummaryVariant,
} from '../../lib/accountProfessionalSummaryLibrary'

import { getSaveHint } from './utils'
import ResumeSectionEmptyHint from './ResumeSectionEmptyHint'

/** Rough heuristic: line-clamp-4 hides content beyond ~4 lines or long paragraphs. */
function libraryVariantNeedsExpand(text: string): boolean {
  if (text.split('\n').length > 4) return true
  return text.length > 200
}

interface ProfessionalSummaryEditorProps {
  data: ProfessionalSummaryEntry[]
  onChange: (data: ProfessionalSummaryEntry[]) => void
  onAddEntryRef?: (fn: () => void) => void
  highlightedSummaryId?: string | null
  accountVariants?: AccountSummaryVariant[]
  accountVariantsLoading?: boolean
}

export default function ProfessionalSummaryEditor({ 
  data, 
  onChange,
  onAddEntryRef,
  highlightedSummaryId,
  accountVariants = [],
  accountVariantsLoading = false,
}: ProfessionalSummaryEditorProps) {
  const entries = Array.isArray(data) ? data : []
  const [searchQuery, setSearchQuery] = useState('')
  const [showSearchInput, setShowSearchInput] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [librarySectionOpen, setLibrarySectionOpen] = useState(false)
  const [expandedLibraryKeys, setExpandedLibraryKeys] = useState<Set<string>>(() => new Set())
  const searchInputRef = useRef<HTMLInputElement>(null)
  const editingEntry = entries.find((entry) => entry.id === editingId)
  const { textareaRef, syncHeight, onResizePointerDown } = useVerticalTextareaResize(
    editingEntry?.text ?? '',
    editingId != null
  )

  const handleAddFromSearch = () => {
    const text = searchQuery.trim()
    if (!text) return

    const newEntry: ProfessionalSummaryEntry = {
      id: `summary-${Date.now()}`,
      text,
      included: true,
    }
    const deselected = entries.map((entry) => ({ ...entry, included: false }))
    onChange([...deselected, newEntry])
    setSearchQuery('')
    setShowSearchInput(true)
  }

  const handleShowSearchInput = () => {
    setShowSearchInput(true)
    setTimeout(() => {
      searchInputRef.current?.focus()
    }, 0)
  }

  // Expose search focus handler to parent via ref (same as Target Title + button)
  useEffect(() => {
    if (onAddEntryRef) {
      onAddEntryRef(handleShowSearchInput)
    }
  }, [onAddEntryRef])

  const handleUpdateEntry = (id: string, text: string) => {
    const updated = entries.map((entry) =>
      entry.id === id ? { ...entry, text } : entry
    )
    onChange(updated)
  }

  // Helper function to deselect entry if others are included
  const deselectEntryIfOthersIncluded = (id: string): boolean => {
    const otherIncluded = entries.filter((e) => e.id !== id && e.included)
    if (otherIncluded.length > 0) {
      const updated = entries.map((entry) =>
        entry.id === id ? { ...entry, included: false } : entry
      )
      onChange(updated)
      return true
    }
    return false
  }

  // Helper function to select entry and deselect all others
  const selectEntryAndDeselectOthers = (id: string) => {
    const updated = entries.map((entry) =>
      entry.id === id ? { ...entry, included: true } : { ...entry, included: false }
    )
    onChange(updated)
  }

  const handleToggleIncluded = (id: string) => {
    const targetEntry = entries.find((entry) => entry.id === id)
    if (!targetEntry) return
    
    // If clicking on an already included entry, check if we can deselect it
    if (targetEntry.included) {
      deselectEntryIfOthersIncluded(id)
      return
    }
    
    // If clicking on a non-included entry, include it and exclude all others (single selection)
    selectEntryAndDeselectOthers(id)
  }

  const handleDeleteEntry = (id: string) => {
    onChange(entries.filter((entry) => entry.id !== id))
    if (editingId === id) {
      setEditingId(null)
    }
  }

  const handleSave = (_id: string) => {
    setEditingId(null)
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    id: string
  ) => {
    if ((e.key === 'Enter' && e.metaKey) || (e.key === 'Enter' && e.ctrlKey)) {
      e.preventDefault()
      handleSave(id)
    }
  }

  const currentNormalizedTexts = useMemo(() => {
    const set = new Set<string>()
    for (const entry of entries) {
      const normalized = normalizeSummaryTextForDedupe(entry.text ?? '')
      if (normalized) set.add(normalized)
    }
    return set
  }, [entries])

  const libraryVariants = useMemo(
    () =>
      accountVariants.filter(
        (variant) => !currentNormalizedTexts.has(normalizeSummaryTextForDedupe(variant.text)),
      ),
    [accountVariants, currentNormalizedTexts],
  )

  const normalizedSearchQuery = searchQuery.trim().toLowerCase()
  const isSearching = normalizedSearchQuery.length > 0

  const textMatchesSearch = (text: string) =>
    text.toLowerCase().includes(normalizedSearchQuery)

  const filteredEntries = isSearching
    ? entries.filter((entry) => textMatchesSearch(entry.text ?? ''))
    : entries

  const filteredLibraryVariants = useMemo(
    () =>
      isSearching
        ? libraryVariants.filter(
            (variant) =>
              textMatchesSearch(variant.text) || textMatchesSearch(variant.sourceLabel),
          )
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- textMatchesSearch derives from searchQuery
    [isSearching, libraryVariants, normalizedSearchQuery],
  )

  const hasSearchResults = filteredEntries.length > 0 || filteredLibraryVariants.length > 0

  const showLibrarySection = accountVariantsLoading || libraryVariants.length > 0

  const handleAdoptLibraryVariant = (variant: AccountSummaryVariant, clearSearch = false) => {
    onChange(adoptAccountSummaryVariant(entries, variant))
    if (clearSearch) {
      setSearchQuery('')
    }
  }

  const handleKeyDownSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddFromSearch()
    }
  }

  const toggleLibraryVariantExpanded = (key: string) => {
    setExpandedLibraryKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const renderLibraryVariantRow = (
    variant: AccountSummaryVariant,
    clearSearchOnAdopt: boolean,
  ) => {
    const inSearchResults = clearSearchOnAdopt && isSearching
    const previewExpanded = expandedLibraryKeys.has(variant.key)
    const canExpand = !inSearchResults && libraryVariantNeedsExpand(variant.text)
    const searchHighlightQuery = searchQuery.trim()

    return (
      <div
        key={variant.key}
        className="group relative flex items-start gap-3 rounded hover:bg-surface-muted p-1"
      >
        <div className="pt-1">
          <CustomCheckbox
            checked={false}
            onChange={() => handleAdoptLibraryVariant(variant, clearSearchOnAdopt)}
            size="xs"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-text-muted mb-1">
            {inSearchResults
              ? highlightSearchQuery(variant.sourceLabel, searchHighlightQuery)
              : variant.sourceLabel}
          </div>
          <div
            className={`text-sm text-text-secondary whitespace-pre-wrap leading-relaxed ${
              previewExpanded || !canExpand ? '' : 'line-clamp-4'
            }`}
          >
            {inSearchResults
              ? highlightSearchQuery(variant.text, searchHighlightQuery)
              : variant.text}
          </div>
          {canExpand && (
            <button
              type="button"
              onClick={() => toggleLibraryVariantExpanded(variant.key)}
              className="mt-1 text-xs text-interactive-link hover:underline"
            >
              {previewExpanded ? 'Show less' : 'Show full text'}
            </button>
          )}
        </div>
      </div>
    )
  }

  const renderEntryRow = (entry: ProfessionalSummaryEntry) => {
    const isEditing = editingId === entry.id
    const isHighlighted = highlightedSummaryId === entry.id
    return (
      <div
        key={entry.id}
        className={`group relative flex items-start gap-3 rounded transition-all duration-300 hover:bg-surface-muted ${
          isHighlighted ? 'bg-brand-primary-subtle ring-2 ring-interactive-focus-ring ring-opacity-50 p-2 -m-2' : ''
        }`}
        data-summary-id={entry.id}
      >
        <div className="pt-1">
          <CustomCheckbox
            checked={entry.included}
            onChange={() => {
              handleToggleIncluded(entry.id)
            }}
            size="xs"
          />
        </div>

        <div className="flex-1 relative">
          {isEditing ? (
            <>
              <textarea
                ref={textareaRef}
                value={entry.text}
                onChange={(e) => {
                  handleUpdateEntry(entry.id, e.target.value)
                  syncHeight()
                }}
                onKeyDown={(e) => {
                  handleKeyDown(e, entry.id)
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    setEditingId(null)
                  }
                }}
                onBlur={() => handleSave(entry.id)}
                placeholder="Enter professional summary text..."
                rows={1}
                className="w-full pl-[5px] pt-0 pb-9 pr-8 text-sm text-text-secondary focus:outline-none resize-none overflow-hidden whitespace-pre-wrap leading-relaxed border border-transparent focus:border-border-default rounded-sm"
                style={{ minHeight: '6rem' }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <button
                type="button"
                aria-label="Resize summary editor height"
                title="Drag to resize"
                onPointerDown={onResizePointerDown}
                onMouseDown={(e) => e.preventDefault()}
                className="absolute bottom-0 left-0 right-8 h-4 flex items-center justify-center cursor-ns-resize touch-none text-text-muted hover:text-text-secondary"
              >
                <span className="w-10 h-0.5 rounded-full bg-border-default" aria-hidden />
              </button>
              <div className="absolute bottom-1 right-2 text-xs text-text-muted pointer-events-none">
                {getSaveHint()}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteEntry(entry.id)
                }}
                className="absolute top-2 right-0 p-1 text-text-muted hover:text-danger opacity-40 hover:opacity-100 transition-opacity"
                title="Delete summary"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </>
          ) : (
            <>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setEditingId(entry.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setEditingId(entry.id)
                  }
                }}
                className={`w-full pt-0 pb-2 pr-8 cursor-text hover:bg-surface-muted transition-colors whitespace-pre-wrap text-sm min-h-[40px] rounded leading-relaxed ${
                  entry.included ? 'text-text-secondary' : 'text-text-muted'
                }`}
              >
                {entry.text ? (
                  isSearching ? (
                    highlightSearchQuery(entry.text, searchQuery.trim())
                  ) : (
                    entry.text
                  )
                ) : (
                  <span className="text-text-muted italic">Click to edit professional summary...</span>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteEntry(entry.id)
                }}
                className="absolute top-2 right-0 p-1 text-text-muted hover:text-danger opacity-0 group-hover:opacity-40 hover:opacity-100 transition-opacity"
                title="Delete summary"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {(showSearchInput || entries.length > 0 || libraryVariants.length > 0) && (
        <div className="relative flex-1">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search or add professional summary..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDownSearch}
            onBlur={() => {
              if (!searchQuery.trim() && entries.length === 0) {
                setShowSearchInput(false)
              }
            }}
            className="w-full px-3 py-2 pr-10 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
          />
          <button
            type="button"
            onClick={handleAddFromSearch}
            disabled={!searchQuery.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-interactive disabled:text-text-muted disabled:cursor-not-allowed transition-colors"
            title="Add summary"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      )}

      {entries.length > 0 || isSearching ? (
        <div className="space-y-3">
          {filteredEntries.map((entry) => renderEntryRow(entry))}

          {isSearching &&
            filteredLibraryVariants.map((variant) => renderLibraryVariantRow(variant, true))}

          {isSearching && !hasSearchResults && (
            <div className="text-sm text-text-muted py-2">No matching summaries found</div>
          )}
        </div>
      ) : showLibrarySection ? (
        <div className="text-sm text-text-muted py-2">
          No summary on this resume yet. Pick one from other saved variants below or use the field above.
        </div>
      ) : null}

      {entries.length === 0 && !isSearching && !showLibrarySection && (
        <ResumeSectionEmptyHint sectionType="professional_summary" className="text-center py-4" />
      )}

      {!isSearching && showLibrarySection && (
        <div className="mt-4 border-t border-border-default pt-3">
          <button
            type="button"
            onClick={() => setLibrarySectionOpen((open) => !open)}
            aria-expanded={librarySectionOpen}
            className="flex w-full items-center gap-2 text-left text-xs font-medium text-text-muted uppercase tracking-wide hover:text-text-secondary transition-colors"
          >
            <motion.svg
              className="w-4 h-4 text-text-muted shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              animate={{ rotate: librarySectionOpen ? 90 : 0 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </motion.svg>
            <span className="flex-1">Other saved summary variants</span>
            {!librarySectionOpen && !accountVariantsLoading && libraryVariants.length > 0 && (
              <span className="normal-case tracking-normal text-text-muted">
                ({libraryVariants.length})
              </span>
            )}
          </button>
          {librarySectionOpen && (
            <div className="mt-2 space-y-2">
              {accountVariantsLoading ? (
                <div className="text-xs text-text-muted">Loading saved variants…</div>
              ) : libraryVariants.length === 0 ? (
                <div className="text-xs text-text-muted">No other saved variants</div>
              ) : (
                libraryVariants.map((variant) => renderLibraryVariantRow(variant, false))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
